import { DOCUMENT_TIME_ZONE } from '@shared/constants/documentTimeZone';
import { DateRange } from './types';

// Wall-clock parts of `instant` in `zone`, read back as if they were UTC.
const zoneWallClockAsUtc = (instant: number, zone: string): number => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(instant))
      .map(({ type, value }) => [type, Number(value)])
  );
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
};

/**
 * Midnight on the 1st of the current month in the firm's zone (ZLAYT-546).
 * The browser zone is not used, so admins in different zones see the same
 * month, matching how document times are shown (see documentTimeZone.ts).
 */
export const startOfMonthInZone = (
  now: Date,
  zone: string = DOCUMENT_TIME_ZONE
): Date => {
  const wall = new Date(zoneWallClockAsUtc(now.getTime(), zone));
  const firstAsUtc = Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), 1);
  // Shift by the zone's offset at that moment; a second pass settles the
  // offset if the first guess landed on the other side of a DST change.
  let start = firstAsUtc - (zoneWallClockAsUtc(firstAsUtc, zone) - firstAsUtc);
  start = firstAsUtc - (zoneWallClockAsUtc(start, zone) - start);
  return new Date(start);
};

export const getDateRange = (period: string): DateRange => {
  const endDate = new Date();
  let startDate;

  switch (period) {
    case '24h':
      startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7d':
      startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      startDate = new Date(endDate.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case 'mtd':
      startDate = startOfMonthInZone(endDate);
      break;
    default:
      // Default to last 24 hours if period is not recognized
      startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
  }

  return {
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
  };
};
